import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp, labelOf } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { formatSAR, statusColor } from "@/lib/utils";
import { toast } from "sonner";
import { ArrowRight, Shield, Clock, Bus, Phone, Mail, MapPin, Star, Plane, Landmark, Route, Quote, BadgeCheck } from "lucide-react";
import { Stars } from "@/components/TestimonialsAdmin";
const SVC_ICON = { airport_pickup: Plane, city_tour: Landmark, intercity: Route };
const SVC_DESC = { airport_pickup: "Bandara Jeddah (KAIA) & Madinah ↔ Hotel Makkah / Madinah.", city_tour: "Ziarah Makkah, Madinah, Thaif & Al Ula bersama driver berpengalaman.", intercity: "Perjalanan Makkah – Madinah nyaman dengan armada pilihan Anda." };
import PackagesSection from "@/components/PackagesSection";
import { ShareWhatsApp } from "@/components/WhatsApp";

export function Home() {
  const { t } = useApp();
  const nav = useNavigate();
  const [fleet, setFleet] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [catalog, setCatalog] = useState(null);
  useEffect(() => {
    api.get("/public/fleet").then((r) => setFleet(r.data.items)).catch(() => {});
    api.get("/public/testimonials").then((r) => setTestimonials(r.data.items)).catch(() => {});
    api.get("/public/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);
  const services = Object.entries(catalog?.services || {}).map(([k, s]) => ({ i: SVC_ICON[k] || Route, k: `svc_${k}`, label: s.label, d: SVC_DESC[k] || s.routes.slice(0, 3).join(" · ") }));
  return (
    <div>
      <section className="abdan-hero-grid">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 mb-6" data-testid="hero-badge">
              <Star className="w-3 h-3 mr-1" /> {t("tagline")}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight">
              {t("hero_title")}
            </h1>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed">{t("hero_sub")}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button onClick={() => nav("/booking")} size="lg" className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-8" data-testid="hero-book">
                {t("book_now")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button onClick={() => nav("/cek-booking")} size="lg" variant="outline" className="rounded-full px-8" data-testid="hero-cek">
                {t("check_booking")}
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12">
              {[{ i: BadgeCheck, l: t("nusuk_registered") }, { i: Shield, l: t("exp_driver") }, { i: Clock, l: t("on_time") }, { i: Bus, l: t("clean_fleet") }].map((f, i) => {
                const I = f.i;
                return (
                  <div key={i} className="flex items-center gap-2 text-slate-700" data-testid={`hero-feature-${i}`}><I className="w-5 h-5 text-emerald-600 shrink-0" /><span className="text-sm font-medium">{f.l}</span></div>
                );
              })}
            </div>
          </div>
          <div className="relative">
            <img src={catalog?.settings?.images?.hero || "https://images.unsplash.com/photo-1565552645632-d725f8bfc19a?w=900"} alt="Masjidil Haram" className="rounded-3xl shadow-2xl w-full h-[420px] object-cover" data-testid="hero-image" />
            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-slate-200">
              <div className="flex items-center justify-between">
                <div><div className="text-xs text-slate-500 uppercase tracking-widest">Booking Otomatis</div><div className="font-bold text-lg">Harga & invoice instan</div></div>
                <div className="flex items-center gap-2 text-emerald-600"><div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /><span className="text-sm font-semibold">24/7</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-2">{t("service")}</div>
          <h2 className="text-3xl sm:text-4xl font-bold">Layanan Umroh & Haji</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const I = s.i;
            return (
              <div key={i} className="abdan-card p-6" data-testid={`home-${s.k}`}>
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center mb-4"><I className="w-5 h-5" /></div>
                <h3 className="font-bold text-xl mb-2">{labelOf(t, s.k, s.label)}</h3><p className="text-slate-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>
      <PackagesSection catalog={catalog} />
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4"><div><div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-2">{t("armada")}</div>
            <h2 className="text-3xl sm:text-4xl font-bold">{t("our_fleet")}</h2><p className="text-slate-500 mt-2">{t("fleet_sub")}</p></div>
            <ShareWhatsApp testid="share-fleet" text={t("share_fleet_msg").replace("{url}", `${window.location.origin}/armada`)} /></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {fleet.map((v) => (
              <div key={v.id} className="abdan-card overflow-hidden group" data-testid={`home-fleet-${v.type}`}>
                <div className="h-44 overflow-hidden bg-slate-100"><img src={v.image} alt={v.model} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" /></div>
                <div className="p-5"><div className="flex items-center justify-between"><div><h3 className="font-bold">{v.model}</h3><div className="text-xs text-slate-500 uppercase">{v.type}</div></div>
                  <div className="text-sm text-slate-600">{t("capacity")} <b>{v.capacity}</b></div></div>
                  {v.description && <p className="text-xs text-slate-600 mt-2 line-clamp-2">{v.description}</p>}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4"><div><div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-2">Testimoni</div>
          <h2 className="text-3xl sm:text-4xl font-bold">{t("testimonials")}</h2><p className="text-slate-500 mt-2">{t("testimonials_sub")}</p></div>
          <ShareWhatsApp testid="share-testimonials" text={t("share_testi_msg").replace("{url}", `${window.location.origin}/#testimoni`)} /></div>
        <div className="grid md:grid-cols-3 gap-6" data-testid="testimonials" id="testimoni">
          {testimonials.map((r) => (
            <div key={r.id} className="abdan-card p-6 flex flex-col" data-testid={`testimonial-${r.id}`}>
              <Quote className="w-6 h-6 text-emerald-300 mb-3" />
              <p className="text-slate-700 leading-relaxed flex-1">“{r.text}”</p>
              <div className="mt-5 pt-4 border-t flex items-center justify-between">
                <div><div className="font-bold">{r.name}</div><div className="text-xs text-slate-500">{r.city}</div></div>
                <Stars n={r.rating} />
              </div>
            </div>))}
        </div>
      </section>
    </div>
  );
}

export function CekBooking() {
  const { t } = useApp();
  const params = new URLSearchParams(window.location.search);
  const [code, setCode] = useState(params.get("code") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e?.preventDefault(); if (!code) return; setLoading(true);
    try {
      const { data } = await api.get(`/public/booking/${code.trim()}`);
      setResult(data.booking);
    } catch { toast.error("Booking not found"); setResult(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("code")) check(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
      <h1 className="text-3xl font-bold mb-2">{t("check_booking")}</h1>
      <p className="text-slate-500 mb-8">Masukkan kode booking Anda (contoh: APB1A2B3C).</p>
      <form onSubmit={check} className="flex gap-2 mb-6">
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="APBXXXXXX" data-testid="cek-code-input" />
        <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cek-submit">{loading ? "..." : t("search")}</Button>
      </form>
      {result && (
        <Card data-testid="cek-result">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-start justify-between">
              <div><div className="text-xs uppercase tracking-widest text-slate-500">{t("booking_code")}</div><div className="font-mono text-xl font-bold">{result.code}</div></div>
              <Badge className={statusColor(result.status)}>{result.status}</Badge>
            </div>
            <div className="grid md:grid-cols-2 gap-4 pt-3 border-t">
              <div><div className="text-xs uppercase text-slate-500">{t("origin")}</div><div className="font-semibold">{result.origin}</div></div>
              <div><div className="text-xs uppercase text-slate-500">{t("destination")}</div><div className="font-semibold">{result.destination}</div></div>
              <div><div className="text-xs uppercase text-slate-500">{t("pickup_date")}</div><div className="font-semibold">{new Date(result.pickup_date).toLocaleString()}</div></div>
              <div><div className="text-xs uppercase text-slate-500">{t("total")}</div><div className="font-semibold text-emerald-700">{formatSAR(result.price)}</div></div>
            </div>
            <Link to={`/invoice/${result.code}`} className="inline-block text-sm font-semibold text-emerald-700 underline" data-testid="cek-invoice-link">{t("invoice")} →</Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function Armada() {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/public/fleet").then((r) => setItems(r.data.items)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div><h1 className="text-3xl font-bold mb-2">{t("our_fleet")}</h1><p className="text-slate-500">{t("fleet_sub")}</p></div>
        <ShareWhatsApp testid="share-fleet-page" text={t("share_fleet_msg").replace("{url}", window.location.href)} />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((v) => (
          <div key={v.id} className="abdan-card overflow-hidden group" data-testid={`fleet-card-${v.plate}`}>
            <div className="h-52 overflow-hidden bg-slate-100">
              <img src={v.image} alt={v.model} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-lg">{v.model}</h3>
                <Badge className={statusColor(v.status)}>{t(`vs_${v.status}`)}</Badge>
              </div>
              <div className="text-sm text-slate-500 font-mono">{v.plate} · {v.year}</div>
              {v.description && <p className="text-sm text-slate-600 mt-2 leading-relaxed" data-testid={`fleet-desc-${v.plate}`}>{v.description}</p>}
              {v.features && <div className="flex flex-wrap gap-1 mt-2">{v.features.split(",").map((f) => f.trim()).filter(Boolean).map((f) => <span key={f} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">{f}</span>)}</div>}
              <div className="flex items-center justify-between mt-4 pt-3 border-t">
                <div className="text-sm text-slate-600">{t("capacity")}: <b>{v.capacity}</b> pax</div>
                <div className="text-xs text-slate-500 uppercase">{v.type}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Contact() {
  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
      <h1 className="text-3xl font-bold mb-2">Hubungi Kami</h1>
      <p className="text-slate-500 mb-10">Tim kami tersedia 24/7 untuk membantu perjalanan Anda.</p>
      <div className="grid md:grid-cols-3 gap-6">
        {[{ i: Phone, t: "WhatsApp", d: "+62 812 7422 6771" }, { i: Mail, t: "Email", d: "abdanpro1@gmail.com" }, { i: MapPin, t: "Office", d: "Makkah, KSA" }].map((c, i) => {
          const I = c.i;
          return (
            <div key={i} className="abdan-card p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center mx-auto mb-4"><I /></div>
              <h3 className="font-bold mb-1">{c.t}</h3><div className="text-slate-600 text-sm">{c.d}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
